import { useQuery } from "@apollo/client";
import a from "indefinite";
import { uniqBy } from "lodash";
import { ReactElement, useState } from "react";
import {
  GetAllOrganizationTeamsQueryQuery,
  GetAllOrganizationTeamsQueryQueryVariables,
} from "types/graphql-schema";

import getAllOrganizationTeamsQuery from "@apps/artifact/graphql/get-all-organization-teams-query";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import ComboboxGeneric, {
  ComboboxGenericButtonProps,
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { assertEdgesNonNull } from "@helpers/helpers";

export type TeamPickerTeam = {
  id: number;
  title: string;
};

export const TeamPickerDropdown = ({
  teams,
  onChange,
  children,
}: {
  teams: TeamPickerTeam[];
  onChange: (value: TeamPickerTeam) => void;
  children: ReactElement<ComboboxGenericButtonProps>;
}) => {
  const currentOrganization = currentOrganizationVar();
  const [options, setOptions] = useState<
    ComboboxGenericOption<TeamPickerTeam>[]
  >([]);
  const teamIds = teams.map(({ id }) => id);
  const [canLoadData, setCanLoadData] = useState(false);

  const { loading } = useQuery<
    GetAllOrganizationTeamsQueryQuery,
    GetAllOrganizationTeamsQueryQueryVariables
  >(getAllOrganizationTeamsQuery, {
    variables: {
      organizationId: currentOrganization.id,
    },
    skip: !canLoadData,
    onCompleted: (data) => {
      const teams = assertEdgesNonNull(data.organization?.teams);
      setOptions(
        uniqBy(teams, "id").map((team) => ({ label: team.title, value: team }))
      );
    },
  });

  const filteredOptions = options.filter(
    (option) => !option.value.id || !teamIds.includes(option.value.id)
  );

  const handleChangeOwner = (option: ComboboxGenericOption<TeamPickerTeam>) => {
    onChange(option.value);
  };

  const handlePreloadData = () => {
    setCanLoadData(true);
  };

  return (
    <ComboboxGeneric
      // eslint-disable-next-line local-rules/use-label
      aria-label="Team picker dropdown"
      loading={loading}
      value={null}
      options={filteredOptions}
      onChangeValue={handleChangeOwner}
      onClickButton={handlePreloadData}
    >
      {children}
    </ComboboxGeneric>
  );
};

const TeamPicker = ({
  teams,
  onRemoveTeam,
  onAddTeam,
  canChange,
}: {
  teams: TeamPickerTeam[];
  onRemoveTeam: (recipient: TeamPickerTeam) => void;
  onAddTeam: (option: TeamPickerTeam) => void;
  canChange: boolean;
}) => {
  const label = useLabel();
  return (
    <div className="flex-1 flex gap-2 flex-wrap">
      {teams.map((team) => (
        <div
          className="border rounded-lg px-2 py-1 text-sm flex items-center gap-1"
          key={team.id}
        >
          {team.title}
          {canChange && (
            <button
              className="ml-0.5 rounded-full p-0.5 border bg-gray-50 text-gray-600 hover:text-white hover:bg-blue-600 hover:border-blue-600"
              onClick={() => onRemoveTeam(team)}
            >
              <TopicflowIcon type="close" className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
      {canChange && (
        <TeamPickerDropdown teams={teams} onChange={onAddTeam}>
          <ComboboxGeneric.Button>
            <TopicflowIcon type="plus" className="h-4 w-4" />
            Pick {a(label("team"))}
          </ComboboxGeneric.Button>
        </TeamPickerDropdown>
      )}
    </div>
  );
};

export default TeamPicker;
